import React from 'react';

/**
 * Email channel mode selection (fast-dev 2026-08-24_18-12 — email modes design).
 *
 * The three user-facing modes map onto the channel schema's two axes:
 *   listen        -> receive_enabled: true,  send_mode: 'disabled'
 *   send_only     -> receive_enabled: false, send_mode: 'tool'
 *   send_receive  -> receive_enabled: true,  send_mode: 'tool' | 'auto'
 *
 * Send-only mailboxes are authenticated with `subscription=skip` (no Graph inbound
 * subscription), so the mode is decided BEFORE OAuth and cannot cross the
 * subscription boundary afterwards — hence `lockedModes` on update.
 */

export type EmailMode = 'listen' | 'send_only' | 'send_receive';
export type EmailReplyBehavior = 'tool' | 'auto';

export const emailModeToFields = (
  mode: EmailMode,
  replyBehavior: EmailReplyBehavior
): { receive_enabled: boolean; send_mode: 'auto' | 'tool' | 'disabled' } => {
  switch (mode) {
    case 'listen': return {
 receive_enabled: true,
send_mode: 'disabled'
};
    case 'send_only': return {
 receive_enabled: false,
send_mode: 'tool'
};
    case 'send_receive': return {
 receive_enabled: true,
send_mode: replyBehavior
};
  }
};

export const emailModeFromFields = (
  receive_enabled?: boolean,
  send_mode?: string
): { mode: EmailMode; replyBehavior: EmailReplyBehavior } => {
  if (receive_enabled === false) return {
 mode: 'send_only',
replyBehavior: 'tool'
};
  if (send_mode === 'auto' || send_mode === 'tool') {
    return {
 mode: 'send_receive',
replyBehavior: send_mode
};
  }
  return {
 mode: 'listen',
replyBehavior: 'tool'
};
};

const MODE_OPTIONS: { value: EmailMode; title: string; description: string }[] = [
  {
    value: 'listen',
    title: 'Listen only',
    description: 'Reads inbound email into the assistant and knowledge base. Never sends.',
  },
  {
    value: 'send_only',
    title: 'Send only',
    description: 'An outbound mailbox for the assistant. Ignores inbound email entirely.',
  },
  {
    value: 'send_receive',
    title: 'Send & receive',
    description: 'Reads inbound email and can reply on the same thread.',
  },
];

const REPLY_OPTIONS: { value: EmailReplyBehavior; title: string; description: string }[] = [
  {
    value: 'tool',
    title: 'Assistant-controlled',
    description: 'The assistant decides per email whether and what to send. Recommended.',
  },
  {
    value: 'auto',
    title: 'Auto-reply',
    description: 'Every assistant response is emailed back automatically, with loop protection.',
  },
];

interface EmailModeSettingsProps {
  mode: EmailMode;
  replyBehavior: EmailReplyBehavior;
  onModeChange: (mode: EmailMode) => void;
  onReplyBehaviorChange: (behavior: EmailReplyBehavior) => void;
  /** Modes not selectable in this context (e.g. crossing the subscription boundary on update). */
  lockedModes?: EmailMode[];
  disabled?: boolean;
}

export const EmailModeSettings = ({
  mode,
  replyBehavior,
  onModeChange,
  onReplyBehaviorChange,
  lockedModes = [],
  disabled = false,
}: EmailModeSettingsProps) => (
    <div className="w-full text-left space-y-3">
        <div className="space-y-1.5">
            <span className="text-[11px] font-semibold text-muted-foreground block">
                What is this channel for? <span className="text-destructive">*</span>
            </span>
            <div className="grid grid-cols-1 gap-2">
                {MODE_OPTIONS.map((opt) => {
            const locked = lockedModes.includes(opt.value);
            return (
                <button
                key={opt.value}
                type="button"
                disabled={disabled || locked}
                onClick={() => onModeChange(opt.value)}
                title={locked ? 'Not available for this channel — the authentication was made for a different mode.' : undefined}
                className={`rounded-lg border p-3 text-left transition ${mode === opt.value ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border hover:border-muted-foreground/40'} ${(disabled || locked) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                    <span className="block text-xs font-semibold text-foreground">{opt.title}</span>
                    <span className="block text-[10px] text-muted-foreground mt-0.5 leading-snug">{opt.description}</span>
                </button>
            );
          })}
            </div>
        </div>
        {mode === 'send_receive' && (
        <div className="space-y-1.5">
            <span className="text-[11px] font-semibold text-muted-foreground block">
                Reply behaviour
            </span>
            <div className="grid grid-cols-2 gap-2">
                {REPLY_OPTIONS.map((opt) => (
                    <button
                key={opt.value}
                type="button"
                disabled={disabled}
                onClick={() => onReplyBehaviorChange(opt.value)}
                className={`rounded-lg border p-3 text-left transition ${replyBehavior === opt.value ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border hover:border-muted-foreground/40'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                        <span className="block text-xs font-semibold text-foreground">{opt.title}</span>
                        <span className="block text-[10px] text-muted-foreground mt-0.5 leading-snug">{opt.description}</span>
                    </button>
          ))}
            </div>
        </div>
      )}
    </div>
);

export default EmailModeSettings;
